// Utilities for evaluating conditional visibility of subquestions in patient forms.
//
// The form_data_model defines optional `conditionsToShow` on subquestions. Each condition
// references another subquestion by `serviceUserVarName` and specifies which answer values
// make the dependent subquestion visible (via `valueIsOneOf`) or invisible (via `valueIsNotOneOf`).
//
// All functions here are pure -- no side effects, no Firestore reads. They operate on
// in-memory form structures, making them safe for use in rendering, progress calculation,
// save paths, and provider-side display.
//
// Key design decisions:
//  - When a subquestion has no `conditionsToShow`, it is always visible (backward compatibility
//    for existing patients whose stored form data predates this feature).
//  - Multiple conditions are AND-joined: all must pass for the subquestion to show.
//  - For selectMultiple gating answers (arrays), visibility passes if ANY selected value
//    satisfies the condition -- matching the intuitive expectation that selecting one relevant
//    option should reveal follow-ups.
//  - Answer shapes vary across the codebase: plain strings, `{value, otherValue}` objects,
//    and arrays. `extractPrimaryValue` normalises these into a comparable form.

/**
 * Recursively unwrap nested {value: {value: ...}} objects to the leaf value.
 *
 * A bug in older save paths could re-wrap selectMultiple answer entries on each
 * save cycle, producing progressively deeper nesting like:
 *   {value: {value: {value: "Asthma"}}}
 *
 * This helper drills through the nesting and returns the innermost non-object
 * value (string, number, null, etc.) or the first object that has no `value`
 * key. Arrays are returned as-is (not recursed into — callers handle arrays
 * at a higher level).
 *
 * Depth-limited to 20 as a safety guard; real-world corruption maxes at ~7
 * levels and Firestore data cannot contain circular references.
 *
 * @param {*} val - A value that may be nested in {value: ...} wrappers
 * @returns {*} The leaf value after unwrapping
 */
export function unwrapNestedValue(val) {
  let depth = 0;
  while (val && typeof val === 'object' && !Array.isArray(val) && 'value' in val && depth < 20) {
    val = val.value;
    depth++;
  }
  return val;
}

/**
 * Normalize a raw Firestore selectMultiple answer array into a clean,
 * deduplicated array of strings suitable for React Hook Form state.
 *
 * Handles all corruption depths: plain strings pass through, {value: "A"}
 * objects are unwrapped once, deeply nested {value: {value: ...}} chains are
 * fully unwrapped. Duplicates (which appear because the corruption bug appends
 * re-selected options alongside their nested originals) are removed.
 *
 * @param {*} arr - Raw answer array from Firestore (may be null/undefined)
 * @returns {string[]} Clean deduplicated array of option label strings
 */
export function normalizeSelectMultipleSelections(arr) {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const result = [];
  for (const item of arr) {
    const leaf = unwrapNestedValue(item);
    if (leaf == null) continue;
    const key = typeof leaf === 'string' ? leaf : String(leaf);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  }
  return result;
}

/**
 * Extract the primary comparable value from a form answer.
 *
 * Answers come in several shapes depending on inputType and otherOptions:
 *  - Plain string (most selectOne answers)
 *  - Object with `{value, otherValue}` (selectOne with an "Other" text field)
 *  - Array of strings or objects (selectMultiple)
 *  - null / undefined (unanswered)
 *
 * Uses unwrapNestedValue so that corrupted nested data ({value: {value: ...}})
 * is handled correctly for visibility checks and progress calculation.
 *
 * @param {*} answer - Raw answer value from subquestion.answer
 * @returns {string|string[]|null} Normalised value for comparison
 */
export function extractPrimaryValue(answer) {
  if (answer === null || answer === undefined) return null;

  if (Array.isArray(answer)) {
    return answer.map((item) => {
      if (item && typeof item === "object" && "value" in item) return unwrapNestedValue(item);
      return item;
    });
  }

  // Modular selectMultiple answers are stored as { selectedOptions: [...] }.
  // Unwrap to a plain array so conditionsToShow comparisons (valueIsNotOneOf
  // etc.) can match individual option strings instead of comparing against
  // the wrapper object.
  if (typeof answer === "object" && Array.isArray(answer.selectedOptions)) {
    return answer.selectedOptions;
  }

  // Modular selectOne answers are stored as { selected: "Other", otherText: "..." }
  // once the respondent has typed into an option's detail box (a plain string
  // before that). The option label is the comparable part; the typed detail
  // never takes part in a visibility rule.
  if (typeof answer === "object" && "selected" in answer) {
    return unwrapNestedValue(answer.selected);
  }

  if (typeof answer === "object" && "value" in answer) {
    return unwrapNestedValue(answer);
  }

  if (typeof answer === "string") return answer;

  return null;
}

/**
 * Build a flat lookup of serviceUserVarName -> extracted primary value
 * across all subquestions in the entire form.
 *
 * This traverses formSubsections -> questions -> subquestions and pulls
 * out the stored answer for each, keyed by its stable serviceUserVarName.
 * The result is used by visibility evaluation to resolve cross-subquestion
 * conditions without needing to know the structural location of each field.
 *
 * @param {object} formSubsections - The formSubsections object from formData[formKey]
 * @returns {object} Map of serviceUserVarName -> extracted primary value
 */
export function buildAnswerLookup(formSubsections) {
  const lookup = {};
  if (!formSubsections || typeof formSubsections !== "object") return lookup;

  Object.values(formSubsections).forEach((subsec) => {
    Object.values(subsec?.questions || {}).forEach((question) => {
      Object.values(question?.subquestions || {}).forEach((sq) => {
        const varName = sq?.serviceUserVarName;
        if (varName) {
          lookup[varName] = extractPrimaryValue(sq?.answer);
        }
      });
    });
  });

  return lookup;
}

/**
 * The condition type that points at an answer on a DIFFERENT form.
 *
 * Everything else (no `conditionType`, or `sameQuestion`) refers to an answer
 * on the form being filled in. Exported so callers can recognise the type
 * without re-typing the string.
 */
export const OTHER_FORM_CONDITION_TYPE = "otherForm";

/**
 * Turn a raw stored answer into the plain option string(s) a condition can
 * compare against.
 *
 * A gating answer can reach the engine in whatever shape the form that owns
 * it saved: a plain string ("Yes"), the modular selectOne shape
 * ({selected: "Other", otherText: "..."}), the adult {value: "Yes"} shape, a
 * selectMultiple wrapper ({selectedOptions: [...]}) or a plain array. Some
 * callers hand over already-extracted values (`buildAnswerLookup`), others
 * hand over the raw Firestore value (GenericFormPage's flat lookup). The
 * engine cannot compare an object against a list of strings, so every shape
 * is unwrapped here, once, for both same-form and cross-form conditions.
 * Mirrors `chosen_values` in python-functions/functions/cyp_form_gating.py.
 *
 * Return contract (the caller decides what each case means):
 *  - `null`  — nothing stored at all (null/undefined). Same-form gates hide on
 *              this; cross-form gates fail open.
 *  - `[]`    — stored, but carrying no option: an empty selection, an empty
 *              string, or an object shape the engine does not recognise.
 *              Judged by the value rules: fails `valueIsOneOf`, passes
 *              `valueIsNotOneOf`, exactly as the raw value always did.
 *  - `[...]` — the chosen option label(s) as strings.
 *
 * @param {*} raw - A stored answer value in any known shape
 * @returns {string[]|null} The option strings, or null when nothing is stored
 */
export function normaliseGatingValues(raw) {
  if (raw === null || raw === undefined) return null;
  const primary = extractPrimaryValue(raw);
  if (primary === null || primary === undefined) return [];
  const values = Array.isArray(primary) ? primary : [primary];
  return values
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map(String);
}

/**
 * Does a list of chosen option strings satisfy a condition's value rules?
 *
 * `valueIsOneOf`: at least one chosen value must be in the list.
 * `valueIsNotOneOf`: none of the chosen values may be in the list.
 * Both may be present; both must hold. List entries are compared as strings
 * so a numeric option in a definition still matches its stored answer.
 *
 * @param {object} condition - A conditionsToShow entry
 * @param {string[]} valuesToCheck - Chosen option strings
 * @returns {boolean}
 */
function valueRulesPass(condition, valuesToCheck) {
  if (Array.isArray(condition.valueIsOneOf)) {
    // At least one selected value must be in the allowed list
    const allowed = condition.valueIsOneOf.map(String);
    if (!valuesToCheck.some((v) => allowed.includes(v))) {
      return false;
    }
  }

  if (Array.isArray(condition.valueIsNotOneOf)) {
    // None of the selected values may be in the excluded list
    const excluded = condition.valueIsNotOneOf.map(String);
    if (valuesToCheck.some((v) => excluded.includes(v))) {
      return false;
    }
  }

  return true;
}

/**
 * Evaluate whether a single subquestion should be visible given the current answers.
 *
 * Checks the subquestion's `conditionsToShow` (if present). Each condition specifies:
 *  - `serviceUserVarName`: the gating subquestion whose answer determines visibility
 *  - `valueIsOneOf`: if set, the gating answer must be one of these values to show
 *  - `valueIsNotOneOf`: if set, the gating answer must NOT be one of these values to show
 *  - `conditionType` (optional): `sameQuestion` (default) reads the gating answer
 *    from this form via `answerLookup`; `otherForm` reads it from the sibling
 *    form named in `formKey` via `externalLookups[formKey]`.
 *
 * For array gating answers (selectMultiple), a condition passes if ANY selected value
 * satisfies the inclusion/exclusion rule.
 *
 * All conditions are AND-joined: every condition must pass for the subquestion to be visible.
 *
 * Returns true (visible) when:
 *  - No conditionsToShow defined (unconditional subquestion or legacy patient data)
 *  - All defined conditions pass
 *
 * Returns false (hidden) when:
 *  - Any condition fails
 *  - A same-form gating answer is null/undefined (unanswered gating question hides dependents)
 *
 * Both branches read the gating answer through `normaliseGatingValues`, so a
 * lookup may hold raw Firestore values ({selected: "Other", otherText: "..."},
 * {value: "Yes"}, {selectedOptions: [...]}) or already-extracted ones; the
 * comparison is always option label against option label.
 *
 * Why `otherForm` conditions are FAIL-OPEN (the opposite of same-form ones):
 * a same-form gate is always answered before its follow-up, so "unanswered"
 * means "not applicable yet" and hiding is right. A sibling form may simply
 * not have been reached, or may be an older snapshot without the question at
 * all. Hiding content on that basis would silently drop questions for every
 * parent who fills the forms in a different order, so an `otherForm` condition
 * can only hide on an EXPLICIT answer: when the sibling form is missing from
 * `externalLookups`, or its answer is empty, the condition passes. This is the
 * rule the CYP ADHD periods questions rely on: they stay visible until the
 * parent has actually said the child's sex assigned at birth is Male or
 * Intersex. The Python engine (cyp_form_gating.py) applies the same rule.
 *
 * @param {object} subquestion - A subquestion object that may have conditionsToShow
 * @param {object} answerLookup - Map of serviceUserVarName -> extracted primary value (this form)
 * @param {object} [externalLookups={}] - Map of formKey -> flat answer lookup for sibling forms
 * @returns {boolean} Whether the subquestion should be visible
 */
export function isSubquestionVisible(subquestion, answerLookup, externalLookups = {}) {
  const conditions = subquestion?.conditionsToShow;
  if (!conditions || typeof conditions !== "object") return true;

  return Object.values(conditions).every((condition) => {
    if (condition.conditionType === OTHER_FORM_CONDITION_TYPE) {
      const siblingLookup = externalLookups?.[condition.formKey];
      // Sibling form not loaded / not referenced: fail-open (see above).
      if (!siblingLookup || typeof siblingLookup !== "object") return true;
      const chosen = normaliseGatingValues(siblingLookup[condition.serviceUserVarName]);
      // Sibling question not answered yet, or answered with nothing: fail-open.
      if (chosen === null || chosen.length === 0) return true;
      return valueRulesPass(condition, chosen);
    }

    const chosen = normaliseGatingValues(answerLookup?.[condition.serviceUserVarName]);

    // Unanswered gating question: hide the dependent subquestion since we
    // can't determine applicability without an answer. (An empty selection is
    // stored, so it is judged by the value rules instead, as it always was.)
    if (chosen === null) return false;

    return valueRulesPass(condition, chosen);
  });
}

/**
 * List the sibling form keys a form's conditions depend on.
 *
 * Walks every subquestion's `conditionsToShow` and collects the `formKey` of
 * each `otherForm` condition. Callers use this to decide which sibling form
 * instances to load into `externalLookups`, and to know whether a form has
 * any cross-form dependency at all (an empty result means the form behaves
 * exactly as before this feature existed).
 *
 * @param {object} formDefinition - A form definition with formSubsections
 * @returns {string[]} Unique sibling form keys, in first-seen order
 */
export function collectOtherFormKeys(formDefinition) {
  const keys = [];
  Object.values(formDefinition?.formSubsections || {}).forEach((subsec) => {
    Object.values(subsec?.questions || {}).forEach((question) => {
      Object.values(question?.subquestions || {}).forEach((sq) => {
        Object.values(sq?.conditionsToShow || {}).forEach((condition) => {
          if (
            condition?.conditionType === OTHER_FORM_CONDITION_TYPE
            && typeof condition.formKey === "string"
            && condition.formKey
            && !keys.includes(condition.formKey)
          ) {
            keys.push(condition.formKey);
          }
        });
      });
    });
  });
  return keys;
}

/**
 * Build the `externalLookups` map for a form from its sibling form instances.
 *
 * Only the sibling forms the definition actually references are included, so
 * a form with no `otherForm` conditions gets an empty object and never pays
 * for building lookups it will not read. A referenced sibling that is absent
 * from `formInstances` is simply left out, which `isSubquestionVisible` treats
 * as fail-open.
 *
 * The caller supplies `buildLookup(siblingEntry)` and receives the sibling's
 * entry from `formInstances` untouched, because the shape answers are stored
 * in differs between pathways: a modular form instance keeps
 * `{ formDefinition, answers }`, while the clinician-side formData map embeds
 * each answer on its subquestion. This keeps the helper pure and free of
 * pathway knowledge.
 *
 * @param {object} formDefinition - The definition of the form being filled in
 * @param {object} formInstances - Map of formKey -> sibling entry (any shape)
 * @param {(siblingEntry: object) => object} buildLookup - Turns one sibling
 *   entry into a flat `{ serviceUserVarName: answer }` lookup
 * @returns {object} Map of formKey -> flat answer lookup
 */
export function buildExternalAnswerLookups(formDefinition, formInstances, buildLookup) {
  const lookups = {};
  collectOtherFormKeys(formDefinition).forEach((siblingKey) => {
    const sibling = formInstances?.[siblingKey];
    if (!sibling || typeof sibling !== "object") return;
    lookups[siblingKey] = buildLookup(sibling);
  });
  return lookups;
}

/**
 * Compute visibility for every subquestion within a question.
 *
 * Returns an object mapping subquestion keys to boolean visibility values,
 * e.g. { '1': true, '2': true, '3': false, '4': false }.
 *
 * @param {object} question - A question object with subquestions
 * @param {object} answerLookup - Map of serviceUserVarName -> extracted primary value
 * @param {object} [externalLookups={}] - Map of formKey -> sibling form lookup (see isSubquestionVisible)
 * @returns {object} Map of subquestion key -> boolean visibility
 */
export function getSubquestionVisibility(question, answerLookup, externalLookups = {}) {
  const result = {};
  const subqs = question?.subquestions || {};

  Object.entries(subqs).forEach(([key, sq]) => {
    result[key] = isSubquestionVisible(sq, answerLookup, externalLookups);
  });

  return result;
}

/**
 * Check whether any subquestion in a question has conditionsToShow rules.
 *
 * This distinguishes conditional questions (e.g. alcohol with gated follow-ups)
 * from non-conditional ones (e.g. name, weight, address). The distinction matters
 * for "answered" semantics: conditional questions use every() (all visible follow-ups
 * must be answered), while non-conditional questions use some() (at least one field
 * filled suffices), since non-conditional questions may have genuinely optional
 * subquestions (middleName, alternate unit fields like kg vs lb).
 *
 * @param {object} question - A question object with subquestions
 * @returns {boolean} True if at least one subquestion has conditionsToShow
 */
export function questionHasConditionalSubquestions(question) {
  return Object.values(question?.subquestions || {}).some(
    (sq) =>
      sq?.conditionsToShow &&
      typeof sq.conditionsToShow === "object" &&
      Object.keys(sq.conditionsToShow).length > 0
  );
}

/**
 * Return only the visible subquestion objects for a question.
 *
 * Used for progress calculation and provider display where we need the actual
 * subquestion data (not just keys) but only for visible entries.
 *
 * @param {object} question - A question object with subquestions
 * @param {object} answerLookup - Map of serviceUserVarName -> extracted primary value
 * @param {object} [externalLookups={}] - Map of formKey -> sibling form lookup (see isSubquestionVisible)
 * @returns {object[]} Array of visible subquestion objects
 */
export function getVisibleSubquestionEntries(question, answerLookup, externalLookups = {}) {
  const subqs = question?.subquestions || {};

  return Object.values(subqs).filter((sq) =>
    isSubquestionVisible(sq, answerLookup, externalLookups)
  );
}

// ─── Validation derivation for FormCurrentQuestionPanel ───────────────────

/**
 * Check whether a single form value counts as "answered".
 *
 * Centralises the answered-check heuristic that was previously duplicated
 * across hasAnyValue, allVisibleAnswered, and completeValidation logic.
 *
 * @param {*} value - A form field value (string, array, or primitive)
 * @returns {boolean} True if the value represents a meaningful user answer
 */
function isValueAnswered(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  return value !== undefined && value !== null;
}

/**
 * Derive all validation and visibility state for the current question.
 *
 * This is a pure function -- no hooks, no side effects, no Firestore reads.
 * It consolidates logic that was previously scattered across three separate
 * useMemo hooks and a useEffect+subscription in FormCurrentQuestionPanel,
 * producing a single object that the component can destructure.
 *
 * Inputs:
 *  - currentQuestion: the question object from the form data model (has .subquestions)
 *  - watchedKeys:     array of React Hook Form field keys for this question's subquestions,
 *                     in the same index order as Object.keys(currentQuestion.subquestions)
 *  - watchedValues:   parallel array of current form values for those keys (from useWatch)
 *  - requiresAllSubquestions: whether this form requires every subquestion to be answered
 *                             (true for BDEFS, WEISS, MDQ, CNST, SPAT)
 *
 * Returns an object with:
 *  - liveAnswerLookup:   { serviceUserVarName: extractedValue } for conditionsToShow evaluation
 *  - showWarningBanner:  true if any hidden conditional subquestion has an existing answer
 *  - hiddenHaveAnswers:  same as showWarningBanner (used for the secondary "will be cleared" text)
 *  - hasAnyValue:        true if at least one subquestion has a non-empty answer
 *  - allVisibleAnswered: true if every visible subquestion has a non-empty answer
 *  - completeValidation: { answeredCount, totalCount, allAnswered, answeredSubquestions }
 *
 * @param {object}   currentQuestion          - Question with .subquestions
 * @param {string[]} watchedKeys              - RHF field keys (parallel to subquestion indices)
 * @param {Array}    watchedValues            - Current form values (parallel to watchedKeys)
 * @param {boolean}  requiresAllSubquestions  - Whether all subquestions must be answered
 * @param {object}   [storedAnswerLookup={}]  - Flat map of serviceUserVarName -> raw answer value
 *   covering ALL questions in the form (not just the current one). Provides
 *   gating values for cross-question conditionsToShow — when a subquestion's
 *   condition references a variable from a different question, the live RHF
 *   values for the current question won't contain it, so we fall back to
 *   this stored lookup. Values are normalised through extractPrimaryValue
 *   internally, so callers can pass raw Firestore shapes.
 * @param {object}   [externalLookups={}]     - Map of formKey -> flat answer lookup for
 *   sibling forms, for `otherForm` conditions (see isSubquestionVisible). Forms
 *   without cross-form conditions pass nothing and behave exactly as before.
 * @returns {object} Derived validation state
 */
export function deriveQuestionValidation(currentQuestion, watchedKeys, watchedValues, requiresAllSubquestions, storedAnswerLookup = {}, externalLookups = {}) {
  const defaults = {
    liveAnswerLookup: {},
    showWarningBanner: false,
    hiddenHaveAnswers: false,
    hasAnyValue: false,
    allVisibleAnswered: false,
    completeValidation: { answeredCount: 0, totalCount: 0, allAnswered: false, answeredSubquestions: {} },
  };

  if (!currentQuestion?.subquestions) return defaults;

  const subquestionEntries = Object.entries(currentQuestion.subquestions);

  // Build a values-by-key map from the parallel arrays.
  // useWatch returns a single value (not an array) when watchedKeys has exactly one entry.
  const valuesByKey = {};
  watchedKeys.forEach((key, i) => {
    valuesByKey[key] = Array.isArray(watchedValues) ? watchedValues[i] : watchedValues;
  });

  // 1. Build liveAnswerLookup: maps serviceUserVarName -> extracted primary value.
  //    This lets isSubquestionVisible evaluate conditionsToShow against the live
  //    form state without waiting for a Firestore save round-trip.
  //
  //    Seed with stored answers from ALL questions so cross-question
  //    conditionsToShow can resolve their gating variable (e.g. Q2's
  //    howLongKnown gating on Q1's relationshipToChild in About you).
  //    Values are normalised through extractPrimaryValue for format
  //    consistency — callers may pass raw Firestore shapes. Live RHF
  //    values for the current question's subquestions overwrite below,
  //    so same-question conditions remain fully reactive.
  const liveAnswerLookup = {};
  Object.entries(storedAnswerLookup).forEach(([varName, value]) => {
    liveAnswerLookup[varName] = extractPrimaryValue(value);
  });
  subquestionEntries.forEach(([sqIdx, sq]) => {
    const answerKey = watchedKeys[subquestionEntries.findIndex(([k]) => k === sqIdx)];
    const watchedValue = answerKey !== undefined ? valuesByKey[answerKey] : undefined;
    if (sq?.serviceUserVarName) {
      liveAnswerLookup[sq.serviceUserVarName] = extractPrimaryValue(
        watchedValue !== undefined ? watchedValue : sq?.answer
      );
    }
  });

  // 2. Determine if any hidden conditional subquestion has an existing answer
  //    (drives the "answers will be cleared" warning banner).
  let anyHiddenWithAnswers = false;
  subquestionEntries.forEach(([sqIdx, sq]) => {
    if (!sq?.conditionsToShow) return;
    const visible = isSubquestionVisible(sq, liveAnswerLookup, externalLookups);
    if (!visible) {
      const answerKey = watchedKeys[subquestionEntries.findIndex(([k]) => k === sqIdx)];
      const val = (answerKey !== undefined ? valuesByKey[answerKey] : undefined) ?? sq?.answer;
      if (val !== null && val !== undefined && val !== '' && val !== 'No answer given' &&
          !(Array.isArray(val) && val.length === 0)) {
        anyHiddenWithAnswers = true;
      }
    }
  });

  // 3. hasAnyValue: at least one subquestion has a non-empty value.
  const hasAnyValue = subquestionEntries.some(([sqIdx]) => {
    const answerKey = watchedKeys[subquestionEntries.findIndex(([k]) => k === sqIdx)];
    return answerKey !== undefined && isValueAnswered(valuesByKey[answerKey]);
  });

  // 4. allVisibleAnswered: every visible subquestion has a non-empty value.
  const visibleEntries = subquestionEntries.filter(
    ([, sq]) => isSubquestionVisible(sq, liveAnswerLookup, externalLookups)
  );
  const allVisibleAnswered = visibleEntries.length > 0 && visibleEntries.every(([sqIdx]) => {
    // Optional subquestions (e.g. "Tell us more" free-text follow-ups marked
    // isOptional in the schema) never block the Save button — the patient can
    // leave them empty and still proceed.
    const sq = currentQuestion.subquestions[sqIdx];
    if (sq?.isOptional) return true;
    const answerKey = watchedKeys[subquestionEntries.findIndex(([k]) => k === sqIdx)];
    return answerKey !== undefined && isValueAnswered(valuesByKey[answerKey]);
  });

  // 5. completeValidation: per-subquestion answered tracking for forms that
  //    require every subquestion to be filled (BDEFS, WEISS, MDQ, etc.).
  let completeValidation = defaults.completeValidation;
  if (requiresAllSubquestions) {
    let answeredCount = 0;
    const answeredSubquestions = {};

    subquestionEntries.forEach(([sqIdx]) => {
      const answerKey = watchedKeys[subquestionEntries.findIndex(([k]) => k === sqIdx)];
      if (answerKey !== undefined && isValueAnswered(valuesByKey[answerKey])) {
        answeredCount++;
        answeredSubquestions[sqIdx] = true;
      }
    });

    completeValidation = {
      answeredCount,
      totalCount: subquestionEntries.length,
      allAnswered: answeredCount === subquestionEntries.length,
      answeredSubquestions,
    };
  }

  return {
    liveAnswerLookup,
    showWarningBanner: anyHiddenWithAnswers,
    hiddenHaveAnswers: anyHiddenWithAnswers,
    hasAnyValue,
    allVisibleAnswered,
    completeValidation,
  };
}

/**
 * Whether a selected option still needs its detail box filled in.
 *
 * Most "tell us more" boxes are optional: ticking the option is a complete
 * answer, and the parent can continue without typing. A subquestion can
 * name options in `requiredOtherOptions` when the tick is meaningless on
 * its own. "Other" on "What helps at home" is that case: the clinician
 * needs to know what the unlisted thing is.
 *
 * An option that is not selected does not count, even if its box is empty.
 * Options that are not listed stay optional, so this does not change any
 * other question.
 *
 * @param {object} subquestion
 * @param {string|string[]|null|undefined} selectedValue
 *   The live answer: a string for selectOne, an array for selectMultiple.
 * @param {Object<string, string>} detailByOption
 *   Option label to whatever is currently in that option's box.
 * @returns {boolean} True when Continue should stay disabled.
 */
export function isRequiredOtherDetailMissing(subquestion, selectedValue, detailByOption) {
  const required = Array.isArray(subquestion?.requiredOtherOptions)
    ? subquestion.requiredOtherOptions
    : [];
  if (required.length === 0) return false;

  const selected = Array.isArray(selectedValue)
    ? selectedValue
    : (selectedValue ? [selectedValue] : []);

  return required.some((option) => {
    if (!selected.includes(option)) return false;
    const detail = detailByOption?.[option];
    return typeof detail !== 'string' || detail.trim() === '';
  });
}
